import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Send, CheckCircle, AlertCircle, ExternalLink,
  HelpCircle, Shield, Mail, Phone
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

/**
 * CONTACT US PAGE — LOCKED DIRECTIVE
 * 
 * - Forces topic selection first
 * - Shows relevant FAQ links after selection
 * - Auto-closes FAQ-covered topics
 * - 7-day response expectation for manual review
 * - No phone support, no live chat, no appeal language
 */

export default function ContactUs() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  
  // Form state
  const [handle, setHandle] = useState('');
  const [realName, setRealName] = useState('');
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [message, setMessage] = useState('');

  useEffect(() => {
    // Pre-fill from user if logged in
    if (user) {
      setHandle(`@${user.username || ''}`);
      setRealName(user.name || '');
    }
    fetchTopics();
  }, [user]);

  const fetchTopics = async () => {
    try {
      const res = await axios.get(`${API}/api/contact/topics`);
      setTopics(res.data.topics || []);
    } catch (error) {
      console.error('Error fetching topics:', error);
      toast.error('Failed to load contact topics');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!handle.trim()) {
      toast.error('Handle (@username) is required');
      return;
    }
    if (!realName.trim()) {
      toast.error('Real name is required');
      return;
    }
    if (!selectedTopic) {
      toast.error('Please select a topic');
      return;
    }
    if (!message.trim() || message.length < 10) {
      toast.error('Message must be at least 10 characters');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const res = await axios.post(`${API}/api/contact/submit`, {
        handle: handle.replace('@', ''),
        real_name: realName,
        topic_id: selectedTopic.id,
        message: message
      }, { headers });

      setSubmissionResult(res.data);
      setSubmitted(true);
      toast.success(res.data.status === 'auto_closed' 
        ? 'Request processed' 
        : 'Request submitted');
    } catch (error) {
      console.error('Error submitting contact:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-600">Loading...</div>
      </div>
    );
  }

  // Success/Auto-closed state
  if (submitted && submissionResult) {
    return (
      <div className="min-h-screen bg-slate-50">
        {/* Header */}
        <header className="sticky top-0 z-40 bg-white border-b border-slate-200">
          <div className="max-w-2xl mx-auto px-4">
            <div className="flex items-center h-14 gap-3">
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-lg text-slate-600 hover:bg-slate-100"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="text-lg font-bold text-slate-800">Contact Us</h1>
            </div>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-4 py-8">
          <div className="bg-white rounded-xl border border-slate-200 p-6 text-center">
            {submissionResult.status === 'auto_closed' ? (
              <>
                <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="w-8 h-8 text-amber-600" />
                </div>
                <h2 className="text-xl font-bold text-slate-800 mb-2">Request Processed</h2>
                <p className="text-slate-600 mb-4">
                  {submissionResult.message}
                </p>
                {submissionResult.faq_link && (
                  <Button
                    onClick={() => navigate(submissionResult.faq_link)}
                    className="bg-amber-600 hover:bg-amber-700 text-white mb-4"
                  >
                    View FAQ / Policy
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                )}
                <div className="bg-slate-50 rounded-lg p-4 text-left text-sm text-slate-600 mt-4">
                  <p className="italic">&quot;{submissionResult.auto_response}&quot;</p>
                </div>
              </>
            ) : (
              <>
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-xl font-bold text-slate-800 mb-2">Request Submitted</h2>
                <p className="text-slate-600 mb-4">
                  {submissionResult.message}
                </p>
                <div className="bg-slate-50 rounded-lg p-4 text-left text-sm text-slate-600">
                  <p className="italic">&quot;{submissionResult.auto_response}&quot;</p>
                </div>
              </>
            )}

            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="mt-6"
            >
              Return to Home
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200">
        <div className="max-w-2xl mx-auto px-4">
          <div className="flex items-center h-14 gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-lg text-slate-600 hover:bg-slate-100"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-lg font-bold text-slate-800">Contact Us</h1>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        {/* Notice Box */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-medium mb-1">Topic-based contact only</p>
              <p>
                Select a topic below. Most questions are answered in our{' '}
                <button onClick={() => navigate('/faq')} className="underline font-medium">FAQ</button> and{' '}
                <button onClick={() => navigate('/about-terms')} className="underline font-medium">Terms &amp; Policies</button>.
              </p>
            </div>
          </div>
        </div>

        {/* Contact Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-6 border-b border-slate-100">
            <h2 className="text-lg font-bold text-slate-800 mb-1">Submit a Request</h2>
            <p className="text-sm text-slate-500">All fields are required</p>
          </div>

          <div className="p-6 space-y-6">
            {/* Handle */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Handle (@username) <span className="text-red-500">*</span>
              </label>
              <Input
                value={handle}
                onChange={(e) => setHandle(e.target.value)}
                placeholder="@yourusername"
                className="w-full"
                data-testid="contact-handle-input"
              />
            </div>

            {/* Real Name */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Real Name <span className="text-red-500">*</span>
              </label>
              <Input
                value={realName}
                onChange={(e) => setRealName(e.target.value)}
                placeholder="Your full name"
                className="w-full"
                data-testid="contact-name-input"
              />
            </div>

            {/* Topic Selection (REQUIRED) */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Topic <span className="text-red-500">*</span>
              </label>
              <div className="space-y-2">
                {topics.map((topic) => (
                  <button
                    key={topic.id}
                    type="button"
                    onClick={() => setSelectedTopic(topic)}
                    className={`w-full p-3 rounded-lg border text-left transition-colors ${
                      selectedTopic?.id === topic.id
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`contact-topic-${topic.id}`}
                  >
                    <span className={`font-medium ${
                      selectedTopic?.id === topic.id ? 'text-indigo-700' : 'text-slate-700'
                    }`}>
                      {topic.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* FAQ Link (shown after topic selection) */}
            {selectedTopic && selectedTopic.faq_link && (
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <HelpCircle className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-slate-600 mb-2">
                      This topic may be covered in our FAQ:
                    </p>
                    <Button
                      type="button"
                      onClick={() => navigate(selectedTopic.faq_link)}
                      variant="outline"
                      size="sm"
                    >
                      View FAQ / Policy
                      <ExternalLink className="w-3 h-3 ml-2" />
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Message <span className="text-red-500">*</span>
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Describe your request in detail..."
                rows={5}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                data-testid="contact-message-input"
              />
              <p className="text-xs text-slate-400 mt-1">
                {message.length}/2000 characters
              </p>
            </div>
          </div>

          {/* Submit */}
          <div className="p-6 bg-slate-50 border-t border-slate-100">
            <Button
              type="submit"
              disabled={submitting || !handle || !realName || !selectedTopic || message.length < 10}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50"
              data-testid="contact-submit-button"
            >
              {submitting ? 'Submitting...' : 'Submit Request'}
              <Send className="w-4 h-4 ml-2" />
            </Button>
            <p className="text-xs text-slate-500 text-center mt-3">
              Response window: up to 7 days. Not all requests receive a reply.
            </p>
          </div>
        </form>

        {/* No Phone Support Notice */}
        <div className="mt-6 p-4 bg-slate-100 rounded-xl">
          <div className="flex items-center gap-2 text-slate-500 text-sm">
            <Phone className="w-4 h-4" />
            <span>Phone support is not available.</span>
          </div>
          <div className="flex items-center gap-2 text-slate-500 text-sm mt-2">
            <Mail className="w-4 h-4" />
            <span>Live chat is not available.</span>
          </div>
        </div>
      </main>
    </div>
  );
}
